import { vatNote } from '../config/vat';
// Microsoft Teams Phone System page content. Structure follows comparable UK Teams calling pages; copy is VOIP2's own.
export const TEAMS_PHONE = {
  hero: {
    title: 'Microsoft Teams,',
    highlight: 'now a phone system',
    sub: 'Call any landline or mobile from the Teams app your staff already use. Keep your existing numbers and add queues, IVRs and recording, all set up for you.',
    image: '/media/teams/teams-hero.webp',
    bullets: ['External calls in Teams', 'Keep your numbers', 'Set up for you', 'UK & international calling'],
  },

  trust: [
    { value: 'From £5', label: `per user per month, ${vatNote(true)}` },
    { value: '1–2 days', label: 'typical time to go live' },
    { value: '140', label: 'countries with VOIP2 numbers' },
  ],

  intro: {
    title: 'Teams is great for meetings. We make it great for calls.',
    paragraphs: [
      'Out of the box, Microsoft Teams calls other Teams users. To ring customers, suppliers and mobiles, it needs a carrier behind it. VOIP2 connects your Teams tenant to the phone network with Direct Routing, so every user gets a real phone number inside Teams.',
      'We design the call flows around how your business works: who answers first, what happens out of hours and where overflow calls go. Your team keeps the app they know, and you keep full control from the Teams admin centre and the VOIP2 portal.',
    ],
    bullets: ['Direct Routing configured for you', 'Call flows and IVRs built to order', 'CRM and contact-centre connectors', 'One portal for numbers and usage'],
  },

  features: {
    title: 'Everything a business phone needs, inside Teams',
    sub: 'Enterprise calling features for teams of any size, with nothing new for your staff to learn.',
    items: [
      { icon: 'Phone', title: 'Real phone numbers', body: 'Give each user a direct number for inbound and outbound calls, at home and abroad.' },
      { icon: 'MessageSquare', title: 'Call from the Teams app', body: 'Dial landlines and mobiles from the same app you use for chat and meetings, on desktop or mobile.' },
      { icon: 'Plug', title: 'Works with what you have', body: 'Connect existing desk phones, a current PBX or a contact centre instead of replacing them.' },
      { icon: 'Repeat', title: 'Keep your numbers', body: 'We port your geographic, national and freephone numbers with no gap in service.' },
      { icon: 'Route', title: 'Queues and auto-attendants', body: 'Ring groups, call queues, IVR menus, opening hours and hold music, all running natively in Teams.' },
      { icon: 'Gauge', title: 'Simple administration', body: 'Manage users and numbers in the Teams admin centre, and see usage and bills in the VOIP2 portal.' },
    ],
  },

  plans: {
    tabs: ['Teams calling', 'Teams + Microsoft 365'],
    note: `Prices per user per month, ${vatNote()}. Annual billing saves around 20%. The Microsoft 365 plans add licensing and onboarding.`,
    'Teams calling': [
      {
        name: 'Basic', monthly: 5, annual: 4, tagline: 'A Teams phone line with pay-as-you-go calls.',
        features: ['Business phone line in Teams', 'Pay-as-you-go calls: UK mobiles 8.5p, landlines 1p', 'Desktop and mobile apps', 'Keep your existing number', 'Self-service portal', 'Presence and messaging', 'Call history'],
      },
      {
        name: 'Pro', monthly: 9, annual: 7, highlight: true, tagline: 'Inclusive minutes and call management.',
        features: ['Everything in Basic, plus:', 'Unlimited UK landline minutes', 'Office manager dashboard', 'On-demand call recording', 'Call queues and ring groups', 'Multi-level IVR', 'Opening hours and hold music'],
      },
      {
        name: 'Enterprise', monthly: 13, annual: 10, tagline: 'Advanced routing for contact centres.',
        features: ['Everything in Pro, plus:', 'Unlimited UK landline and mobile minutes', 'Advanced queues and wallboards', 'Supervisor listen, whisper and barge', 'Contact-centre connectors', 'API and CRM integrations', 'Priority UK support with SLA'],
      },
    ],
    'Teams + Microsoft 365': [
      {
        name: 'Basic', monthly: 6, annual: 5, tagline: 'Teams calling with Microsoft 365 licensing.',
        features: ['Everything in Teams calling Basic', 'Microsoft 365 Business licence', 'Guided Teams onboarding', 'Direct Routing set-up', 'Keep your existing number', 'Self-service portal', 'Call history'],
      },
      {
        name: 'Pro', monthly: 10, annual: 8, highlight: true, tagline: 'The usual choice for hybrid teams.',
        features: ['Everything in Basic, plus:', 'Unlimited UK landline minutes', 'Office manager dashboard', 'On-demand call recording', 'Call queues and ring groups', 'Multi-level IVR', 'Opening hours and hold music'],
      },
      {
        name: 'Enterprise', monthly: 14, annual: 11, tagline: 'Full unified communications and contact centre.',
        features: ['Everything in Pro, plus:', 'Unlimited UK landline and mobile minutes', 'Advanced queues and wallboards', 'Supervisor listen, whisper and barge', 'Contact-centre connectors', 'API and CRM integrations', 'Priority UK support with SLA'],
      },
    ],
  },

  steps: [
    { verb: 'Plan', body: 'We agree your numbers, users and call flows, and check your Microsoft licences.' },
    { verb: 'Connect', body: 'We set up Direct Routing to your Teams tenant and port or assign your numbers.' },
    { verb: 'Call', body: 'Your team makes and takes external calls in Teams, usually within a day or two.' },
  ],

  faqs: [
    { q: 'What is Teams Direct Routing?', a: 'Direct Routing links a phone carrier, in this case VOIP2, to Microsoft Teams. Your staff can then call and be called by any landline or mobile from the Teams app, not only other Teams users.' },
    { q: 'Do I need Microsoft licences?', a: 'Each user needs Microsoft Teams and a Teams Phone licence. If you do not have them, our Teams + Microsoft 365 plans include the licensing and we handle the onboarding.' },
    { q: 'Can I keep my current numbers?', a: 'Yes. We port geographic, national and 0800 numbers to VOIP2 and route them into Teams without losing service.' },
    { q: 'How long does it take to set up?', a: 'Most set-ups are live within one or two working days once numbers and licences are confirmed. We do the configuration for you.' },
    { q: 'Can I add call queues and IVRs?', a: 'Yes. Auto-attendants, call queues, ring groups, call recording and wallboards are all available, managed from one portal.' },
    { q: 'Can I use international numbers?', a: 'Yes. You can route VOIP2 numbers from 140 countries into Teams, including toll-free and UIFN numbers.' },
  ],

  userOptions: ['1–5 users', '6–15 users', '16–50 users', '51–100 users', '100+ users'],

  related: [
    { label: 'Cloud PBX', to: '/products/cloud-pbx' },
    { label: 'SIP Trunking', to: '/products/sip-trunking' },
    { label: 'DID International Numbers', to: '/phone-numbers/did-international' },
  ],
};
